import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getSponsorsTaskFactory } from '~admin/interactor/sponsor/perform-get-sponsors';
import { SponsorData } from '~admin/model/sponsor-data';
import { Box } from '~common/components/box.component';
import { useTask } from '~common/hook/use-task';
import { TaskObserver } from '~web/view/components/core/task-observer.component';
import { OverlayProps } from '~web/view/components/layout/overlay.component';
import { TaskOverlay } from '~web/view/components/layout/task-overlay.component';
import { ScrollBar } from '~web/view/theme/utils';

interface SponsorImageProps {
  sponsor: SponsorData;
  onSponsorSelected?: (sponsor: SponsorData) => void;
}

interface SponsorPickerProps extends OverlayProps {
  onSponsorSelected?: (sponsor: SponsorData) => void;
}

const SponsorImage: React.FunctionComponent<SponsorImageProps> = ({
  sponsor,
  onSponsorSelected,
}) => {
  const [error, setError] = useState<boolean>(undefined);

  if (error) {
    return null;
  }

  return (
    <ImageContainer>
      <Image
        src={sponsor.icon.url}
        alt={sponsor.url}
        onError={(): void => {
          setError(true);
        }}
        onClick={(): void => {
          onSponsorSelected?.(sponsor);
        }}
        loading={'lazy'}
      />
    </ImageContainer>
  );
};

export const SponsorPicker: React.FunctionComponent<SponsorPickerProps> = ({
  onSponsorSelected,
  ...rest
}) => {
  const getSponsorsTask = useTask(getSponsorsTaskFactory);

  useEffect((): void => {
    getSponsorsTask();
  }, []);

  return (
    <StyledOverlay task={getSponsorsTask} {...rest}>
      <Container>
        <TaskObserver task={getSponsorsTask}>
          {(sponsors: SponsorData[]): ReactNode => (
            <SponsorList>
              {sponsors.map(
                (sponsor): ReactNode => (
                  <SponsorImage
                    key={sponsor.id}
                    sponsor={sponsor}
                    onSponsorSelected={onSponsorSelected}
                  />
                ),
              )}
            </SponsorList>
          )}
        </TaskObserver>
      </Container>
    </StyledOverlay>
  );
};

const StyledOverlay = styled(TaskOverlay)`
  .overlay-scrollable-content {
    overflow: hidden;
  }
`;

const ImageContainer = styled(Box)`
  flex: 1 1 170px;
  align-items: center;
  justify-content: center;
  margin: 4px;
  padding: 16px;

  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  object-fit: contain;
  cursor: pointer;
`;

const Container = styled(Box)`
  height: 420px;
  margin-top: 16px;
  ${ScrollBar};
`;

const SponsorList = styled(Box)`
  flex: 1 1 auto;
  flex-wrap: wrap;
`;



// WEBPACK FOOTER //
// ./view/components/details/sponsor-details/sponsor-picker.component.tsx